/* eslint-disable no-console */
import {
  HttpTransportType,
  HubConnectionBuilder,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';

import { config } from '@app/config';
import { SIGNAL_R_HOST_CHUNK } from '@app/constants';
import { getAccessToken } from '@app/utils/accessTokenStorage';
import { defineHostWithProtocol } from '@app/utils/defineHostWithProtocol/defineHostWithProtocol';

let hubConnectionInstance: HubConnection | null = null;
let isConnecting = false;
let connectionPromise: Promise<void> | null = null;
let subscriptions: {
  [eventName: string]: { callback: (args?: unknown) => void; args: unknown[] };
} = {};
// Для методов из MULTI_ARGS_METHODS хранятся ВСЕ наборы аргументов (ключ —
// JSON аргументов): лид может слушать несколько клиентов через ListenClient,
// и после реконнекта нужно восстановить каждую группу, а не только последнюю.
// Остальные методы хранят только последний вызов (прежняя семантика): иначе
// реестр рос бы без ограничения — например, ListenLeadsGroup получает новый
// SearchId на каждый поиск, и реплей всех старых на реконнекте вреден.
const MULTI_ARGS_METHODS = new Set(['ListenClient']);

let invokeMethods: {
  [methodName: string]: Map<string, unknown[]>;
} = {};

type WsReconnectListener = () => void;
let reconnectListeners: WsReconnectListener[] = [];

// Подписка на успешное переподключение SignalR: сервер мог отправить события,
// пока соединения не было, — подписчики дозапрашивают пропущенное сами.
export const subscribeToWsReconnect = (
  listener: WsReconnectListener
): (() => void) => {
  reconnectListeners.push(listener);

  return () => {
    reconnectListeners = reconnectListeners.filter(
      (registered) => registered !== listener
    );
  };
};

const waitForConnection = (
  hubConnection: HubConnection
): Promise<HubConnection> => {
  return new Promise<HubConnection>((resolve, reject) => {
    const checkConnection = setInterval(() => {
      if (hubConnection.state === 'Connected') {
        clearInterval(checkConnection);

        resolve(hubConnection);
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkConnection);

      reject(new Error('Connection timeout'));
    }, 10000);
  });
};

export const createWSConnection = async (
  onSuccess: (hubConnection: HubConnection) => void,
  onError?: (error: Error) => void
): Promise<HubConnection | null> => {
  if (hubConnectionInstance) {
    try {
      await waitForConnection(hubConnectionInstance);

      onSuccess(hubConnectionInstance);

      return hubConnectionInstance;
    } catch (error) {
      if (typeof onError === 'function') {
        onError(error as Error);
      }

      return null;
    }
  }

  if (isConnecting && connectionPromise) {
    try {
      await connectionPromise;
      await waitForConnection(hubConnectionInstance!);

      onSuccess(hubConnectionInstance!);

      return hubConnectionInstance;
    } catch (error) {
      if (typeof onError === 'function') {
        onError(error as Error);
      }

      return null;
    }
  }

  const generateReconnectIntervals = () => {
    const intervals = [];
    let interval = 0;

    while (interval < 60) {
      intervals.push(interval * 1000);
      interval += 10;
    }

    while (intervals.length < 600) {
      intervals.push(60 * 1000);
    }

    return intervals;
  };

  isConnecting = true;

  hubConnectionInstance = new HubConnectionBuilder()
    .withUrl(
      defineHostWithProtocol({ isWebSocket: true }) + SIGNAL_R_HOST_CHUNK,
      {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        // JWT пользователя для авторизации подключения (us/6370). Фабрика
        // вызывается при каждом подключении и переподключении, поэтому после
        // перелогина соединение поднимается уже с новым токеном.
        accessTokenFactory: () => getAccessToken() ?? '',
      }
    )
    .configureLogging(
      config.showConsoleLogs ? LogLevel.Information : LogLevel.Error
    )
    .withAutomaticReconnect(generateReconnectIntervals())
    .build();

  hubConnectionInstance.onreconnected(async () => {
    await reconnectSignalR();
  });

  connectionPromise = hubConnectionInstance.start();

  try {
    await connectionPromise;
    await waitForConnection(hubConnectionInstance);

    isConnecting = false;

    onSuccess(hubConnectionInstance);
  } catch (error) {
    isConnecting = false;
    hubConnectionInstance = null;
    connectionPromise = null;

    console.debug('Error while establishing connection to websocket', error);

    if (typeof onError === 'function') {
      onError(error as Error);
    }
  }

  return hubConnectionInstance;
};

export const invokeWSMethod = async (
  methodName: string,
  ...args: unknown[]
) => {
  if (!hubConnectionInstance) {
    throw new Error('WebSocket connection is not established');
  }

  // Регистрируем вызов ДО invoke: если сам вызов упал (например, во время
  // обрыва), после реконнекта он всё равно будет повторён.
  if (!invokeMethods[methodName]) {
    invokeMethods[methodName] = new Map();
  }
  if (!MULTI_ARGS_METHODS.has(methodName)) {
    invokeMethods[methodName].clear();
  }
  invokeMethods[methodName].set(JSON.stringify(args), [...args]);

  try {
    await hubConnectionInstance.invoke(methodName, ...args);

    if (
      methodName === 'ListenOrganization' &&
      !subscriptions.WhatsAppSenderQR
    ) {
      subscribeToWsEvent('WhatsAppSenderQR', () => {});
    }

    if (methodName === 'ListenUser' && !subscriptions.OnFrontendUpdate) {
      subscribeToWsEvent('OnFrontendUpdate', () => {});
    }
  } catch (error) {
    console.error(`Error invoking ${methodName}:`, error);
  }
};

// Снять вызов с реестра восстановления (например, при размонтировании
// карточки лида): реконнект перестаёт перевызывать этот набор аргументов.
export const unregisterWSMethod = (methodName: string, ...args: unknown[]) => {
  const methodInvocations = invokeMethods[methodName];

  if (!methodInvocations) return;

  methodInvocations.delete(JSON.stringify(args));

  if (methodInvocations.size === 0) {
    delete invokeMethods[methodName];
  }
};

/* eslint-disable @typescript-eslint/no-explicit-any */
type WsEventCallback = (...args: any[]) => void;

// Мультиподписчики поверх легаси-слота: subscribeToWsEvent исторически держит
// ОДИН обработчик на имя события (replace-семантика, на неё полагаются
// потребители, переподписывающиеся без отписки), а addWsEventListener
// добавляет независимых слушателей с отпиской по колбэку — для случаев, когда
// одно событие нужно нескольким смонтированным компонентам (issue/10450:
// карточка лида + чат, встроенный в карточку клиента).
let multiSubscriptions: { [eventName: string]: Set<WsEventCallback> } = {};

// Один hub.on на имя события: читает оба реестра динамически, поэтому
// добавление/удаление слушателей не требует переподвешивания.
const attachWsEventDispatcher = (eventName: string) => {
  if (!hubConnectionInstance) return;

  hubConnectionInstance.off(eventName);
  hubConnectionInstance.on(eventName, (...args: any[]) => {
    const single = subscriptions[eventName];

    if (single) {
      subscriptions[eventName] = { callback: single.callback, args };
      single.callback(...args);
    }

    const listeners = multiSubscriptions[eventName];

    if (listeners) {
      for (const listener of [...listeners]) {
        try {
          listener(...args);
        } catch (error) {
          console.error(`Error in ws listener for ${eventName}:`, error);
        }
      }
    }
  });
};

const hasWsEventConsumers = (eventName: string): boolean =>
  Boolean(subscriptions[eventName] || multiSubscriptions[eventName]?.size);

export const subscribeToWsEvent = (
  eventName: string,
  callback: (...args: any) => void
) => {
  if (hubConnectionInstance) {
    subscriptions[eventName] = { callback, args: [] };

    attachWsEventDispatcher(eventName);
  }
};

export const unsubscribeFromWsEvent = (eventName: string) => {
  if (hubConnectionInstance && subscriptions[eventName]) {
    delete subscriptions[eventName];

    // Мультиподписчики того же события продолжают жить: снимаем hub.on
    // только когда потребителей не осталось.
    if (hasWsEventConsumers(eventName)) {
      attachWsEventDispatcher(eventName);
    } else {
      hubConnectionInstance.off(eventName);
    }
  }
};

export const addWsEventListener = (
  eventName: string,
  listener: WsEventCallback
): (() => void) => {
  if (!multiSubscriptions[eventName]) {
    multiSubscriptions[eventName] = new Set();
  }
  multiSubscriptions[eventName].add(listener);

  if (hubConnectionInstance) {
    attachWsEventDispatcher(eventName);
  }

  return () => {
    const listeners = multiSubscriptions[eventName];

    if (!listeners) return;

    listeners.delete(listener);

    if (listeners.size === 0) {
      delete multiSubscriptions[eventName];

      if (hubConnectionInstance && !hasWsEventConsumers(eventName)) {
        hubConnectionInstance.off(eventName);
      }
    }
  };
};

export const reconnectSignalR = async () => {
  if (!hubConnectionInstance) {
    throw new Error('WebSocket connection is not established');
  }

  if (config.showConsoleLogs) {
    console.log('Reconnecting to SignalR');
  }

  for (const methodName in invokeMethods) {
    if (invokeMethods.hasOwnProperty(methodName)) {
      for (const methodArgs of invokeMethods[methodName].values()) {
        try {
          if (config.showConsoleLogs) {
            console.log('Re-invoking ws method: ', methodName, methodArgs);
          }

          await hubConnectionInstance.invoke(methodName, ...methodArgs);
        } catch (error) {
          console.error(`Error re-invoking ${methodName}:`, error);
        }
      }
    }
  }

  const eventNames = new Set([
    ...Object.keys(subscriptions),
    ...Object.keys(multiSubscriptions),
  ]);

  for (const eventName of eventNames) {
    if (config.showConsoleLogs) {
      console.log('Re-subscribing to ws event: ', eventName);
    }

    attachWsEventDispatcher(eventName);
  }

  for (const listener of [...reconnectListeners]) {
    try {
      listener();
    } catch (error) {
      console.error('Error in ws reconnect listener:', error);
    }
  }
};

export const disconnectSignalR = async () => {
  if (hubConnectionInstance) {
    try {
      // 1. Отключаем приём событий
      for (const eventName in subscriptions) {
        hubConnectionInstance.off(eventName);
      }
      for (const eventName in multiSubscriptions) {
        hubConnectionInstance.off(eventName);
      }
      subscriptions = {};
      multiSubscriptions = {};
      invokeMethods = {};

      // 2. Останавливаем соединение (graceful)
      await hubConnectionInstance.stop();
      if (config.showConsoleLogs) console.log('SignalR connection stopped');
    } catch (err) {
      console.error('Error while stopping SignalR:', err);
    } finally {
      // 3. Сбрасываем синглтон-ссылки
      hubConnectionInstance = null;
      isConnecting = false;
      connectionPromise = null;
    }
  }
};
